import { TOption, valueOf } from "@/types/common";

/**
 * 請求設定 (invoice settings) client-side enums & mock data.
 *
 * Mock-first: values here mirror the intended backend contract, and labels are
 * mirrored so the UI renders synchronously. When wiring the API, resolve options
 * via `useEnum(<EnumName>)` and replace the MOCK_* lists below.
 */

/** 請求月 / 支払締切（月）— relative month offset (spec No.1 / No.4: 5 options). */
export const BILLING_MONTH = {
  BEFORE_PREV: "before_prev",
  PREV: "prev",
  CURRENT: "current",
  NEXT: "next",
  AFTER_NEXT: "after_next",
} as const;

export type TBillingMonth = valueOf<typeof BILLING_MONTH>;

export const BILLING_MONTH_LABEL: Record<TBillingMonth, string> = {
  [BILLING_MONTH.BEFORE_PREV]: "前々月",
  [BILLING_MONTH.PREV]: "前月",
  [BILLING_MONTH.CURRENT]: "当月",
  [BILLING_MONTH.NEXT]: "翌月",
  [BILLING_MONTH.AFTER_NEXT]: "翌々月",
};

export const BILLING_MONTH_OPTIONS: TOption[] = Object.values(BILLING_MONTH).map((value) => ({
  value,
  label: BILLING_MONTH_LABEL[value],
}));

/**
 * 入退社・一時帰国・休職の請求方法 — how a partial month is billed.
 * 全額請求する / 請求しない / 日割り.
 */
export const BILLING_TREATMENT = {
  FULL: "full",
  NONE: "none",
  PRORATED: "prorated",
} as const;

export type TBillingTreatment = valueOf<typeof BILLING_TREATMENT>;

export const BILLING_TREATMENT_LABEL: Record<TBillingTreatment, string> = {
  [BILLING_TREATMENT.FULL]: "全額請求する",
  [BILLING_TREATMENT.NONE]: "請求しない",
  [BILLING_TREATMENT.PRORATED]: "日割り",
};

export const BILLING_TREATMENT_OPTIONS: TOption[] = Object.values(BILLING_TREATMENT).map((value) => ({
  value,
  label: BILLING_TREATMENT_LABEL[value],
}));

/**
 * 請求書番号設定 — insertable code chips. 連番/{n} = zero-padded sequence of length n
 * (連番/4〜連番/6). Only one 連番 may be used at a time.
 */
export const INVOICE_NUMBER_CODES: string[] = ["年", "取引先コード", "連番/4", "連番/5", "連番/6"];

/** Regex matching a 連番/{n} token — used for the "exactly one 連番" rule. */
export const SEQUENCE_TOKEN_REGEX = /\{連番\/\d+\}/g;

/** 連番 validation messages (spec No.41). */
export const SEQUENCE_REQUIRED_MESSAGE = "連番は必ず1つ必要です。";
export const SEQUENCE_ONLY_ONE_MESSAGE = "連番は1つだけご設定ください。";

/** 請求日 / 支払締切（日）— 1〜29 validation message (spec No.31 / No.34). */
export const BILLING_DAY_RANGE_MESSAGE = "1〜29の数値を入力してください。";
export const BILLING_DAY_PATTERN = /^([1-9]|1[0-9]|2[0-9])$/;

/** Default 請求書番号 format when unset. */
export const DEFAULT_INVOICE_NUMBER_FORMAT = "INV-{年}-{連番/6}";

/** Static preview shown under the number format (mock). */
export const INVOICE_NUMBER_PREVIEW = "INV-2026-000001";

/** 適用中のフロー — the fixed v1 billing flow chain (承認フロー steps are v2, excluded). */
export const BILLING_FLOW_STEPS: string[] = ["下書き", "確認中", "送付前", "入金待ち", "完了"];

/** Shared placeholder used by the design (選択内容). */
export const SELECT_PLACEHOLDER = "選択内容";

/** Toast on save (mock). */
export const INVOICE_SAVE_SUCCESS_MESSAGE = "請求設定を保存しました。";
